import { ButtonHTMLAttributes, ReactNode } from "react";
import { Loader2 } from "lucide-react";
import { NormalButton } from "@/components/NormalButton";
import type { NormalButtonProps } from "@/components/NormalButton";
import { Button } from "@/components/Button";
import type { ButtonProps } from "@/components/Button";
import { cn } from "@/utils/cn";

type VariantButtonProps = Omit<NormalButtonProps, "borderColor" | "bgColor" | "hoverBgColor"> & {
  leftIcon?: ReactNode;
  rightIcon?: ReactNode;
};

/** pillWarm palette button */
export const PillWarmButton = ({
  children,
  className,
  textColor = "text-theme-btn-warm-text",
  ...props
}: VariantButtonProps) => (
  <NormalButton
    borderColor="border-theme-btn-warm-border"
    bgColor="bg-theme-btn-warm-bg"
    textColor={textColor}
    hoverBgColor="hover:bg-theme-btn-warm-hover"
    size="sm"
    className={cn("h-8 px-3 py-1 text-xs font-inter min-w-0 rounded-lg", className)}
    {...props}
  >
    {children}
  </NormalButton>
);

/** message palette button — purple/pab-msg tokens */
export const MessageButton = ({
  children,
  className,
  textColor = "text-theme-btn-message-text",
  ...props
}: VariantButtonProps) => (
  <NormalButton
    borderColor="border-theme-btn-message-border"
    bgColor="bg-theme-btn-message-bg"
    textColor={textColor}
    hoverBgColor="hover:bg-theme-btn-message-hover"
    size="sm"
    className={cn("h-8 px-3 py-1 text-xs font-inter min-w-0 rounded-lg", className)}
    {...props}
  >
    {children}
  </NormalButton>
);

/** secondary back button — used as Back navigation button */
export const SecondaryButton = ({
  children,
  className,
  borderColor = "border-theme-btn-secondary-border",
  bgColor = "bg-theme-btn-secondary-bg",
  textColor = "text-theme-btn-secondary-text",
  hoverBgColor = "hover:bg-theme-btn-secondary-hover",
  ...props
}: NormalButtonProps) => (
  <NormalButton
    borderColor={borderColor}
    bgColor={bgColor}
    textColor={textColor}
    hoverBgColor={hoverBgColor}
    className={cn(
      "flex-shrink-0 w-[103px] h-[58px] rounded-[14px] font-semibold leading-[24px] tracking-[-0.3px] transition-colors",
      className,
    )}
    {...props}
  >
    {children}
  </NormalButton>
);

/** Join button — ghost style, adapts per theme */
export const JoinButton = ({
  children,
  className,
  textColor = "text-theme-btn-ghost-text",
  ...props
}: VariantButtonProps) => (
  <NormalButton
    type="button"
    borderColor="border-theme-btn-ghost-border"
    bgColor="bg-theme-btn-ghost-bg"
    textColor={textColor}
    hoverBgColor="hover:bg-theme-btn-ghost-hover"
    size="sm"
    className={cn("h-8 px-3 py-1 text-xs font-inter min-w-0 rounded-lg", className)}
    {...props}
  >
    {children}
  </NormalButton>
);

/** accent/session palette button */
export const AccentButton = ({
  children,
  className,
  disabled,
  textColor = "text-theme-btn-session-text",
  ...props
}: VariantButtonProps) => (
  <NormalButton
    borderColor="border-theme-btn-session-border"
    bgColor="bg-theme-btn-session-bg"
    textColor={textColor}
    hoverBgColor={disabled ? "" : "hover:bg-theme-btn-session-hover"}
    size="sm"
    className={cn("h-8 px-4 py-1 text-xs font-inter", className)}
    disabled={disabled}
    {...props}
  >
    {children}
  </NormalButton>
);

type LoginButtonVariant = "linkedin" | "primary";

interface LoginButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: LoginButtonVariant;
  isLoading?: boolean;
  leftIcon?: ReactNode;
  rightIcon?: ReactNode;
}

const loginButtonVariantClasses: Record<LoginButtonVariant, string> = {
  linkedin: "bg-linkedin",
  primary: "bg-primary-gradient",
};

/** Login page CTA — 48px tall, 12px radius (Figma AchieveOS Sign In, node 163:28).
 *  Self-contained so its sizing doesn't collide with Button/NormalButton size classes. */
export const LoginButton = ({
  children,
  variant = "primary",
  isLoading = false,
  leftIcon,
  rightIcon,
  className,
  disabled,
  type = "button",
  ...props
}: LoginButtonProps) => (
  <button
    type={type}
    className={cn(
      "inline-flex h-12 w-full items-center justify-center rounded-[12px] px-4 text-14 font-semibold text-white transition-opacity duration-200 hover:opacity-90",
      leftIcon ? "gap-2.5" : "gap-1.5",
      "focus:outline-none focus-visible:ring-2 focus-visible:ring-theme-login-brand-text focus-visible:ring-offset-2",
      "disabled:pointer-events-none disabled:opacity-50",
      loginButtonVariantClasses[variant],
      className,
    )}
    disabled={disabled || isLoading}
    {...props}
  >
    {isLoading ? <Loader2 className="size-5 animate-spin" aria-hidden="true" /> : leftIcon}
    {children}
    {!isLoading && rightIcon}
  </button>
);

/** Invite Achievers gradient button — blue-to-purple diagonal (172deg), matches Figma program published modal */
export const InviteGradientButton = ({
  children,
  className,
  ...props
}: Omit<ButtonProps, "variant">) => (
  <Button variant="inviteGradient" className={cn(className)} {...props}>
    {children}
  </Button>
);
